/// <reference path="./shims.d.ts" />
/* eslint-disable max-lines-per-function */
import _ from 'lodash';
import psHttp, { psAxios } from '@patsnap/common-ps-http';
import { createUniformSetting } from '@patsnap/uniform-setting';
import { HeaderService, SLD, Site, type ListItem } from '@patsnap-utils/biz';
import { prepareTokenBase, updateTokenBaseRequestHeaders } from './enable-token-base';
import { isUserLogin, isAnonymousError, AnonymousError } from './user-auth';
import { handleLogout } from '@patsnap-utils/passport';
import { setWebsiteFont } from './configure-website-font';
import { productShellDeferred, accountDeferred, prepareTokenDeferred } from './defer';
import type { IProjectPreset } from './enable-token-base';
import { injectService } from '@patsnap/tahiti-core';
import {
    EurekaAmountsControlApi,
    PackageInfoService,
    SubscriptionInfoService,
    UserInfoService,
} from '@telecom/shared/domain/permission';
import type { EurekaAmountsControl, IPackageType } from '@telecom/shared/domain/permission';
import type { AxiosInstance } from 'axios';
import type { T_Locale, T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import '@patsnap-ui/icon/assets/solid/HistoryRecord.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/MemberPoint.svg';
import { ejectPassportInterceptor } from './interceptors';
import { buildUserCenterMenuItems } from './user-center-menu';

export interface IStartupOption<T = unknown> {
    /**
     * 是否禁用token拦截器
     * @description 在分享页面中，禁用token拦截器
     */
    disablePrepareToken?: boolean
    headerService: HeaderService
    projectName: string
    preset: IProjectPreset
    pageInit: () => Promise<T>
}

interface StartupHandlerOptions<T> {
    projectName: string;
    headerService: HeaderService
    locale: T_Locale
    setting: ReturnType<typeof createUniformSetting>
    pageData: T
    userCenterMenuState: UserCenterMenuState
}

interface UserCenterMenuState {
    packages: IPackageType[];
    packagesLoaded: boolean;
    control: EurekaAmountsControl | null;
}

type StartupResponse<T> = [
    HeaderService,
    T_Locale,
    T,
    ReturnType<typeof createUniformSetting>
];

window.productShellReady = productShellDeferred;

const supportLocales: T_Locale[] = ['cn', 'en', 'jp', 'tw'];

const homePageHref = new Site({ sld: SLD.ANALYTICS }).href;

async function getPrepareTokenBase() {
    return prepareTokenBase().then((passport) => {
        prepareTokenDeferred.resolve(passport);
        return passport;
    }).catch(e => {
        if (isAnonymousError(e)) {
            ejectPassportInterceptor();
            accountDeferred.reject(e);
            prepareTokenDeferred.reject(e);
            return;
        }
        throw e;
    });
}

async function initializeHeaderService(headerService: HeaderService) {
    headerService.logoHref = homePageHref;
    try {
        await headerService.sync({
            http: psHttp,
            uri: import.meta.env.VITE_ACCOUNT_SERVICE_URI as string,
        });
    } catch (error) {
        headerService.syncing = true;
        throw error;
    } finally {
        headerService.account && accountDeferred.resolve(headerService.account);
    }
}

async function initializeSetting() {
    const setting = createUniformSetting({
        verbose: true,
        axiosInstance: psAxios as AxiosInstance as any,
        loggedIn: true,
        baseAccountUrl: import.meta.env.VITE_ACCOUNT_SERVICE_URI,
        locale: {
            supports: supportLocales,
        },
        region: import.meta.env.VITE_DEPLOY_ENV as T_Region,
    });

    const settingData = await setting.locale.getLocale();
    const locale = settingData.user || settingData.default;

    setWebsiteFont(locale);
    updateTokenBaseRequestHeaders({ 'X-Site-Lang': locale.toUpperCase() });

    return { locale, setting };
}

function startupHandler<T>(options: StartupHandlerOptions<T>): StartupResponse<T> {
    const { projectName, headerService, locale, setting, pageData, userCenterMenuState } = options;

    // 注入 UserInfoService 用于判断用户类型
    const userInfoService = injectService<UserInfoService>(UserInfoService);
    if (userCenterMenuState.packagesLoaded) {
        userInfoService.setPackageInfo(userCenterMenuState.packages);
    }

    /* WhiteLabel 时，隐藏 PatSnap Logo */
    if (headerService.whiteLabel) {
        headerService.logoVisible = false;
    }

    /* 注册网站语言 */
    const { locales } = headerService;
    headerService.locales = _.filter(locales, locale => _.includes(supportLocales, locale.key)).map(({ ...rest }) => {
        return {
            ...rest,
            active: rest.key === locale,
        };
    });

    /* 注册切换语言函数 */
    headerService.beforeLocaleChange = async (localeKey: string, done: Function) => {
        await setting.locale.setLocale(localeKey as T_Locale);
        window.location.reload();
        done();
    };

    /* 注册登出回调函数 */
    headerService.userCenterService.logoutHandler = async (done: Function, logoutHref: string) => {
        const data: unknown = await handleLogout();
        setTimeout(() => {
            const href = _.get(data, 'data.redirect_url', logoutHref);
            window.location.href = href;
        }, 1000);
    };

    /* 自定义用户中心菜单 */
    const buildin = headerService.userCenterService.beforeRenderMenuItems;
    const isEnterpriseTenant = userInfoService.isEnterpriseTenant();
    const isPersonalTenant = userInfoService.isPersonalTenant();
    headerService.userCenterService.beforeRenderMenuItems = function beforeRenderMenuItems(items: ListItem[]) {
        const _items = buildin(items);

        return buildUserCenterMenuItems({
            items: _items,
            locale,
            isEnterpriseTenant,
            isPersonalTenant,
            packages: userCenterMenuState.packages,
            packagesLoaded: userCenterMenuState.packagesLoaded,
            control: userCenterMenuState.control,
            createEurekaHref: pathname => new Site({ sld: SLD.EUREKA, pathname }).href,
        }) as ListItem[];
    };

    /* header头切换租户成功后页面跳转 */
    headerService.switchTenantHandler = async (tenantId, done) => {
        await done();
        window.location.reload();
        // window.location.href = homePageHref;
    };

    /* 九宫格的微前端在header信息拿到之前就立马加载的话，会劫持defer.js和favorites-browser.js，导致部分页面七鱼样式加载不出来 */
    if (headerService.account) {
        productShellDeferred.resolve(async () => {
            const { defer } = await import(/* webpackChunkName: "defer" */'./defer');
            defer({ projectName }, headerService.account!, locale);
        });
    }

    return [headerService, locale, pageData, setting];
}
async function initSubscription(headerService: HeaderService) {
    // 个人租户需要获取订阅信息
    if (headerService.account?._info.company_id) {
        return;
    }
    // 预热订阅信息
    const subscriptionInfoService = injectService<SubscriptionInfoService>(SubscriptionInfoService);
    subscriptionInfoService.getCurrentSubscription();
}

async function prepareUserCenterMenuState(isLogin: boolean): Promise<UserCenterMenuState> {
    if (!isLogin) {
        return {
            packages: [],
            packagesLoaded: false,
            control: null,
        };
    }

    const packageInfoService = injectService<PackageInfoService>(PackageInfoService);
    const eurekaAmountsControlApi = injectService<EurekaAmountsControlApi>(EurekaAmountsControlApi);
    const [packagesResult, controlResult] = await Promise.allSettled([
        packageInfoService.getPackageInfo(),
        eurekaAmountsControlApi.getAmountsControl(),
    ]);

    if (packagesResult.status === 'rejected') {
        console.warn('[startup] Failed to load package info for user center menu:', packagesResult.reason);
    }
    if (controlResult.status === 'rejected') {
        console.warn('[startup] Failed to load security control for user center menu:', controlResult.reason);
    }

    return {
        packages: packagesResult.status === 'fulfilled' ? packagesResult.value : [],
        packagesLoaded: packagesResult.status === 'fulfilled',
        control: controlResult.status === 'fulfilled' ? controlResult.value : null,
    };
}

async function startup<PageData>(startupOption: IStartupOption<PageData>) {
    if (!startupOption.disablePrepareToken) {
        await getPrepareTokenBase();
    }

    const headerService = startupOption.headerService;
    const projectName = startupOption.projectName;
    const isLogin = await isUserLogin();
    try {
        const tasks = isLogin ? [initializeHeaderService(headerService),initSubscription(headerService)]: [];
        const data = await Promise.all([
            startupOption.pageInit(),
            initializeSetting(),
            ...tasks,
        ]);
        const [pageData, { locale, setting }] = data;
        const userCenterMenuState = await prepareUserCenterMenuState(isLogin);
        return startupHandler({
            projectName,
            headerService,
            locale,
            setting,
            pageData,
            userCenterMenuState,
        });
    } catch (error) {
        console.error(error);
        throw error;
    }

}

export {
    startup,
    AnonymousError,
    isAnonymousError,
};
